import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { Mail, ArrowRight, CheckCircle2, AlertTriangle } from "lucide-react";
import { lookupInvite, acceptInvite } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

const ROLE_LABEL = { view: "View only", edit: "Can edit" };

export default function InvitePage() {
  const { token } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [state, setState] = useState({ status: "loading" });
  const [accepting, setAccepting] = useState(false);

  useEffect(() => {
    let cancelled = false;
    lookupInvite(token)
      .then((data) => {
        if (cancelled) return;
        setState({ status: "ready", invite: data });
      })
      .catch((err) => {
        if (cancelled) return;
        const code = err?.response?.status;
        const detail = err?.response?.data?.detail;
        let message = "Invite not found.";
        if (code === 410) message = detail || "This invitation has expired or been revoked.";
        else if (code === 404) message = "Invite not found.";
        setState({ status: "error", message });
      });
    return () => {
      cancelled = true;
    };
  }, [token]);

  const goToAuth = (mode = "login") => {
    const next = `/invite/${token}`;
    navigate(`/auth?redirect=${encodeURIComponent(next)}&mode=${mode}`, { replace: true });
  };

  const accept = async () => {
    setAccepting(true);
    try {
      const res = await acceptInvite(token);
      toast.success("Invite accepted");
      navigate(`/projects/${res.project_id}`, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail || "Could not accept invite";
      toast.error(detail);
      setAccepting(false);
    }
  };

  if (state.status === "loading" || user === null) {
    return (
      <div className="min-h-screen grid place-items-center text-[#A8A29E] text-sm" data-testid="invite-loading">
        Loading invitation…
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="min-h-screen fc-app-bg grid place-items-center px-4" data-testid="invite-error">
        <div className="fc-card p-8 max-w-md w-full text-center">
          <div className="w-12 h-12 rounded-full bg-amber-500/10 border border-amber-500/30 grid place-items-center mx-auto">
            <AlertTriangle className="w-5 h-5 text-amber-700" />
          </div>
          <h2 className="fc-heading text-lg text-[#1C1C1C] mt-4">Invite unavailable</h2>
          <p className="text-sm text-[#57534E] mt-2">{state.message}</p>
          <Button
            data-testid="invite-error-home-btn"
            onClick={() => navigate("/")}
            className="mt-6 bg-[#1C1C1C] text-white hover:bg-[#2A2A2E]"
          >
            Go home
          </Button>
        </div>
      </div>
    );
  }

  const invite = state.invite;
  const loggedInEmail = (user?.email || "").toLowerCase();
  const inviteEmail = (invite?.email || "").toLowerCase();
  const matches = !!user && loggedInEmail === inviteEmail;
  const wrongAccount = !!user && !matches;

  return (
    <div className="min-h-screen fc-app-bg grid place-items-center px-4" data-testid="invite-page">
      <div className="fc-card p-8 max-w-md w-full">
        <div className="w-12 h-12 rounded-full bg-sage-100 border border-sage-200 grid place-items-center">
          <Mail className="w-5 h-5 text-sage-700" />
        </div>
        <div className="fc-label mt-5">Project invitation</div>
        <h2 className="fc-heading text-2xl text-[#1C1C1C] mt-1.5" data-testid="invite-project-name">
          {invite.project_name || "Untitled Project"}
        </h2>
        {invite.project_address && (
          <p className="text-sm text-[#78716C] mt-1">{invite.project_address}</p>
        )}
        <div className="mt-4 flex items-center gap-2 flex-wrap text-xs">
          <span className="px-2 py-0.5 rounded-full fc-goal-pill" data-testid="invite-role">
            {ROLE_LABEL[invite.role] || "View only"}
          </span>
          <span className="text-[#A8A29E]">for</span>
          <span className="text-[#1C1C1C] font-medium" data-testid="invite-email">
            {invite.email}
          </span>
        </div>

        {invite.status === "accepted" && (
          <div className="mt-6 rounded-lg bg-emerald-500/10 border border-emerald-500/20 text-[#059669] px-3 py-2 text-sm flex items-center gap-2" data-testid="invite-already-accepted">
            <CheckCircle2 className="w-4 h-4" /> You've already accepted this invite.
          </div>
        )}

        <div className="mt-6 flex flex-col gap-2">
          {!user && (
            <>
              <Button
                data-testid="invite-signin-btn"
                onClick={() => goToAuth("login")}
                className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E]"
              >
                Sign in to accept <ArrowRight className="w-4 h-4 ml-1.5" />
              </Button>
              {!invite.user_exists && (
                <Button
                  data-testid="invite-signup-btn"
                  onClick={() => goToAuth("signup")}
                  variant="outline"
                  className="border-black/[0.08] hover:bg-sage-50"
                >
                  Create an account first
                </Button>
              )}
              <p className="text-[11px] text-[#A8A29E] mt-1">
                Sign in with <strong>{invite.email}</strong> — that's the email this invite was sent to.
              </p>
            </>
          )}

          {matches && invite.status !== "accepted" && (
            <Button
              data-testid="invite-accept-btn"
              onClick={accept}
              disabled={accepting}
              className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E]"
            >
              {accepting ? "Accepting…" : "Accept and open project"} <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          )}

          {matches && invite.status === "accepted" && (
            <Button
              data-testid="invite-open-btn"
              onClick={() => navigate(`/projects/${invite.project_id}`, { replace: true })}
              className="bg-[#1C1C1C] text-white hover:bg-[#2A2A2E]"
            >
              Open project <ArrowRight className="w-4 h-4 ml-1.5" />
            </Button>
          )}

          {wrongAccount && (
            <div data-testid="invite-wrong-account" className="rounded-lg bg-amber-500/10 border border-amber-500/30 text-amber-800 px-3 py-2 text-sm">
              You're signed in as <strong>{user.email}</strong>, but this invite was sent to <strong>{invite.email}</strong>.
              <div className="mt-2">
                <button
                  type="button"
                  data-testid="invite-switch-account-btn"
                  onClick={() => goToAuth("login")}
                  className="underline hover:text-[#1C1C1C]"
                >
                  Sign in with {invite.email}
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
